import { DestroyRef, inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import {
  BimplusSignalrClient,
  ProjectItemSignalrData,
  SignalRClientConfiguration,
  TransportType,
} from 'bimplus-signalr-client';
import { TEAM_FACADE_TOKEN } from '@web-portal/domains/team/anti-corruption-layer';
import { ProjectEventBus } from '@web-portal/domains/shared/event-bus';
import { BIMPLUS_API, BIMPLUS_API_CONFIG } from '@web-portal/domains/shared/bimplus-core';
import { Project } from '@web-portal/domains/shared/domain-models';

export type SignalrClientFactory = (config: SignalRClientConfiguration) => BimplusSignalrClient;

export const SIGNALR_CLIENT_FACTORY = new InjectionToken<SignalrClientFactory>(
  'ProjectSignalrClientFactory',
  { providedIn: 'root', factory: () => config => new BimplusSignalrClient(config) }
);

export class ProjectSignalrService {
  private readonly api = inject(BIMPLUS_API);
  private readonly apiConfig = inject(BIMPLUS_API_CONFIG);
  private readonly teamFacade = inject(TEAM_FACADE_TOKEN);
  private readonly projectEventBus = inject(ProjectEventBus);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientFactory = inject(SIGNALR_CLIENT_FACTORY);

  private readonly selectedProject = new BehaviorSubject<Project | null>(null);

  setSelectedProject(project: Project | null): void {
    this.selectedProject.next(project);
  }

  private client: BimplusSignalrClient | null = null;
  private connectingPromise: Promise<unknown> | null = null;
  private connected = false;
  private currentGroupKey: string | null = null;

  init(): void {
    const routeIds = this.getRouteIdsFromLocation();
    const baseUrl = `${this.apiConfig.protocol}${this.apiConfig.host}/`;
    this.client = this.clientFactory({
      hubName: 'ProjectHub',
      baseUrl,
      signalrTransports: [TransportType.WebSockets],
      useFirefoxWorkaround: false,
      options: {
        keepAliveIntervalInMilliseconds: 15_000,
        serverTimeoutInMilliseconds: 60_000,
      },
      reconnect: () => this.onReconnect(),
      eventsList: [
        {
          eventName: 'ProjectIssueChanged',
          eventHandler: (data: ProjectItemSignalrData) =>
            this.projectEventBus.emit({ type: '[Project] issue-changed', payload: data }),
        },
        {
          eventName: 'ProjectSceneChanged',
          eventHandler: (data: ProjectItemSignalrData) =>
            this.projectEventBus.emit({ type: '[Project] scene-changed', payload: data }),
        },
        {
          eventName: 'ProjectCommentChanged',
          eventHandler: (data: ProjectItemSignalrData) =>
            this.projectEventBus.emit({ type: '[Project] comment-changed', payload: data }),
        },
        {
          eventName: 'ProjectWorkFlowChanged',
          eventHandler: (data: ProjectItemSignalrData) =>
            this.projectEventBus.emit({ type: '[Project] workflow-changed', payload: data }),
        },
        {
          eventName: 'ProjectWorkFlowStepChanged',
          eventHandler: (data: ProjectItemSignalrData) =>
            this.projectEventBus.emit({ type: '[Project] workflow-step-changed', payload: data }),
        },
      ],
    });
    this.client.initialize(() => {
      return this.api.getAccessToken();
    });
    combineLatest([toObservable(this.teamFacade.selectedTeam), this.selectedProject])
      .pipe(
        map(([team, project]) => ({
          teamId: team?.id ?? routeIds.teamId,
          projectId: project?.id ?? routeIds.projectId,
        })),
        filter(({ teamId, projectId }) => !!teamId && !!projectId),
        distinctUntilChanged(
          (previous, current) =>
            previous.teamId === current.teamId && previous.projectId === current.projectId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ teamId, projectId }) => {
        if (teamId && projectId) {
          this.connectAndListen(teamId, projectId);
        }
      });
    this.destroyRef.onDestroy(() => {
      this.currentGroupKey = null;
      this.client?.disconnect();
    });
  }

  private connectAndListen(teamId: string, projectId: string): void {
    const newKey = `${teamId}:${projectId}`;

    if (this.connected) {
      if (this.currentGroupKey === newKey) {
        return;
      }
      // Project changed while connected — disconnect before joining the new group.
      this.connected = false;
      this.currentGroupKey = null;
      this.client!.disconnect();
      this.connectAndListen(teamId, projectId);
      return;
    }

    if (this.connectingPromise) {
      this.connectingPromise = this.connectingPromise.then(() => {
        if (this.connected) {
          this.client?.startListenProject(teamId, projectId);
          this.currentGroupKey = newKey;
        }
      });
      return;
    }

    if (this.client) {
      this.connectingPromise = this.client
        .connect()
        .then(() => {
          this.connected = true;
          this.currentGroupKey = newKey;
          this.client?.startListenProject(teamId, projectId);
        })
        .catch(err => {
          console.error('ProjectHub connection failed:', err);
        })
        .finally(() => {
          this.connectingPromise = null;
        });
    }
  }

  private onReconnect(): void {
    // The library uses withAutomaticReconnect() internally, so the connection
    // is re-established automatically. We only need to re-register the project listener.
    const routeIds = this.getRouteIdsFromLocation();
    const teamId = this.teamFacade.selectedTeam()?.id ?? routeIds.teamId;
    const projectId = this.selectedProject.getValue()?.id ?? routeIds.projectId;
    if (teamId && projectId) {
      this.currentGroupKey = `${teamId}:${projectId}`;
      this.client?.startListenProject(teamId, projectId);
    }
  }

  private getRouteIdsFromLocation(): { teamId?: string; projectId?: string } {
    const location = globalThis.location;

    if (!location) {
      return {};
    }

    const candidates = [location.pathname, location.hash.replace(/^#/, '')];
    for (const candidate of candidates) {
      const ids = this.extractRouteIdsFromPath(candidate);
      if (ids.teamId && ids.projectId) {
        return ids;
      }
    }

    return {};
  }

  private extractRouteIdsFromPath(path: string): { teamId?: string; projectId?: string } {
    const routePattern = /(?:^|\/)teams\/([^/?#]+)\/projects\/([^/?#]+)/;
    const match = routePattern.exec(path);
    if (!match) {
      return {};
    }

    return {
      teamId: this.safeDecodeUriComponent(match[1]),
      projectId: this.safeDecodeUriComponent(match[2]),
    };
  }

  private safeDecodeUriComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
